import type { ConfigNetworkName } from './configMatrix';

export const FRONTEND_NETWORKS = ['regtest', 'mutiny', 'testnet4', 'main'] as const satisfies readonly ConfigNetworkName[];

export type FrontendNetwork = (typeof FRONTEND_NETWORKS)[number];

export interface FrontendDeployment {
  network: FrontendNetwork;
  bridge: {
    instanceId: string;
    apiUrl: string;
    anvilRpcUrl: string;
  };
}

interface FrontendDeploymentInput {
  network?: string;
  bridgeInstanceId?: string;
  bridgeApiUrl?: string;
  bridgeAnvilRpcUrl?: string;
}

const FRONTEND_NETWORK_SET = new Set<string>(FRONTEND_NETWORKS);
const BRIDGE_INSTANCE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const DEFAULT_ANVIL_RPC_URL = 'http://localhost:8545';

export function parseFrontendDeployment(input: FrontendDeploymentInput): FrontendDeployment {
  const network = parseNetwork(input.network);
  const instanceId = parseBridgeInstanceId(input.bridgeInstanceId);
  const apiUrl = parseBridgeApiUrl(input.bridgeApiUrl);
  const anvilRpcUrl = parseBridgeAnvilRpcUrl(input.bridgeAnvilRpcUrl);

  return {
    network,
    bridge: {
      instanceId,
      apiUrl,
      anvilRpcUrl,
    },
  };
}

export function formatFrontendNetwork(network: FrontendNetwork): string {
  switch (network) {
    case 'mutiny':
      return 'Mutinynet';
    case 'main':
      return 'Mainnet';
    case 'testnet4':
      return 'Testnet4';
    case 'regtest':
      return 'Regtest';
  }
}

function parseNetwork(value: string | undefined): FrontendNetwork {
  if (!value || !FRONTEND_NETWORK_SET.has(value)) {
    throw new Error(`NEXT_PUBLIC_NETWORK must be one of: ${FRONTEND_NETWORKS.join(', ')}`);
  }
  return value as FrontendNetwork;
}

function parseBridgeInstanceId(value: string | undefined): string {
  if (!value || !BRIDGE_INSTANCE_PATTERN.test(value)) {
    throw new Error('NEXT_PUBLIC_UNIT_BRIDGE_INSTANCE_ID must be a canonical lowercase slug');
  }
  return value;
}

function parseBridgeApiUrl(value: string | undefined): string {
  if (!value || value.trim() !== value) {
    throw new Error('NEXT_PUBLIC_UNIT_BRIDGE_API_URL must be an absolute HTTP(S) URL');
  }

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error('NEXT_PUBLIC_UNIT_BRIDGE_API_URL must be an absolute HTTP(S) URL');
  }

  if (
    (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') ||
    parsed.username !== '' ||
    parsed.password !== '' ||
    parsed.hash !== '' ||
    parsed.search !== ''
  ) {
    throw new Error('NEXT_PUBLIC_UNIT_BRIDGE_API_URL must be an absolute credential-free HTTP(S) URL without a query or fragment');
  }

  return value.endsWith('/') ? value.slice(0, -1) : value;
}

function parseBridgeAnvilRpcUrl(value: string | undefined): string {
  const candidate = value ?? DEFAULT_ANVIL_RPC_URL;
  if (candidate.trim() !== candidate) {
    throw new Error('NEXT_PUBLIC_UNIT_BRIDGE_ANVIL_RPC_URL must be an absolute credential-free HTTP(S) URL');
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    throw new Error('NEXT_PUBLIC_UNIT_BRIDGE_ANVIL_RPC_URL must be an absolute credential-free HTTP(S) URL');
  }

  if (
    (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') ||
    parsed.username !== '' ||
    parsed.password !== '' ||
    parsed.hash !== '' ||
    parsed.search !== ''
  ) {
    throw new Error('NEXT_PUBLIC_UNIT_BRIDGE_ANVIL_RPC_URL must be an absolute credential-free HTTP(S) URL without a query or fragment');
  }

  return candidate.endsWith('/') ? candidate.slice(0, -1) : candidate;
}

export const FRONTEND_DEPLOYMENT = parseFrontendDeployment({
  network: process.env.NEXT_PUBLIC_NETWORK,
  bridgeInstanceId: process.env.NEXT_PUBLIC_UNIT_BRIDGE_INSTANCE_ID,
  bridgeApiUrl: process.env.NEXT_PUBLIC_UNIT_BRIDGE_API_URL,
  bridgeAnvilRpcUrl: process.env.NEXT_PUBLIC_UNIT_BRIDGE_ANVIL_RPC_URL,
});

export function getFrontendNetwork(): FrontendNetwork {
  return FRONTEND_DEPLOYMENT.network;
}
